/**
 * O humor de hoje — o `mood_picker.dart` e o `mood_service.dart` do Flutter.
 *
 * Fica aqui, e não em cada app, porque a mesma pergunta é feita em três
 * lugares: o painel do site, o painel do celular e a página "Hoje" do relógio.
 * A tabela dos cinco humores já existia em Swift (`ConteudoView.swift`) e o
 * jeito de gravar já existia no `Relogio.tsx`; separados, o dia em que alguém
 * trocar um emoji vai deixar dois apps discordando de um terceiro.
 *
 * O documento gravado é o `UserMood.toMap()` do Flutter, campo por campo — é o
 * mesmo que os três painéis leem, e mudar um nome de campo aqui apagaria o
 * histórico de humor de quem já respondeu.
 *
 * Estes cinco emoji ficam emoji, e a casa não os troca por traço como fez com
 * os corredores do mercado: aqui o emoji **é o dado**. Ele desce para o
 * `moodEmoji` do documento, e é isso que o app do Flutter e o relógio leem de
 * volta para mostrar o humor de ontem. Um traço no lugar deles não caberia num
 * campo de texto do banco, e trocá-los reescreveria o passado.
 */

import type { Armazem } from '../armazem';
import { COLECOES } from '../types/colecoes';
import { lerData } from './tempo';

export interface Humor {
  emoji: string;
  /** De 1 a 5, como o `score` de cada `HeartEmoji` do `MoodPicker`. */
  nota: number;
  /**
   * O `glowColor` do `HeartEmoji`: a cor do halo de quem está escolhido. Só a
   * tabela abaixo tem uma — quem grava um humor manda emoji e nota, e por isso
   * o campo é opcional. O relógio não desenha halo nenhum, então lá não entra.
   */
  brilho?: string;
}

/**
 * Os cinco, na ordem de lá. As notas são o que fica gravado, então a ordem não
 * é enfeite: trocar duas de lugar reescreve o passado de quem já respondeu.
 *
 * Os brilhos são as cores do Material que o `MoodPicker` pede por nome —
 * `purple[200]`, `blueGrey[100]`, `pink[100]`, `pink[300]` e `pinkAccent` —,
 * escritas aqui em hexadecimal porque fora do Flutter esses nomes não existem.
 */
export const HUMORES: Humor[] = [
  { emoji: '💜', nota: 1, brilho: '#ce93d8' },
  { emoji: '☁️', nota: 2, brilho: '#cfd8dc' },
  { emoji: '🌸', nota: 3, brilho: '#f8bbd0' },
  { emoji: '💖', nota: 4, brilho: '#f06292' },
  { emoji: '✨', nota: 5, brilho: '#ff4081' },
];

/**
 * O documento gravado, como ele volta da coleção. É o `UserMood.fromMap()` de
 * lá: tudo opcional porque o que veio do Flutter não passou por esta casa.
 */
export interface DocDeHumor {
  username?: string;
  moodScore?: number;
  moodEmoji?: string;
  timestamp?: string;
}

/**
 * O humor mais recente de cada pessoa, pelo slug em minúsculas.
 *
 * Estava copiado nos dois painéis. Virou um só quando apareceu o terceiro
 * leitor — o Kovu flutuante, que precisa do emoji de quem mora com você para
 * dizer "Fulana está 💖 hoje!". Três cópias divergiriam na primeira correção.
 *
 * O `quando` sai junto porque quem lê precisa das duas coisas: o emoji para
 * mostrar e a data para o `disseHoje`.
 */
export function humoresMaisRecentes<T extends DocDeHumor>(
  documentos: ReadonlyArray<{ dados: T }>,
): Map<string, { doc: T; quando: Date }> {
  const mapa = new Map<string, { doc: T; quando: Date }>();
  for (const { dados } of documentos) {
    const quando = lerData(dados.timestamp);
    const nome = (dados.username ?? '').toLowerCase();
    if (!quando || !nome) continue;
    const atual = mapa.get(nome);
    if (!atual || quando > atual.quando) mapa.set(nome, { doc: dados, quando });
  }
  return mapa;
}

/** Grava o humor de alguém. Devolve o id do documento, como o `adicionar`. */
export function dizerHumor(armazem: Armazem, quem: string, humor: Humor): Promise<string> {
  return armazem.adicionar(COLECOES.humores, {
    username: quem,
    moodScore: humor.nota,
    moodEmoji: humor.emoji,
    timestamp: new Date().toISOString(),
  });
}

/**
 * Se esse humor é de hoje — o `hasSubmittedToday` de lá.
 *
 * Lá a conta é sobre a coleção inteira e espera o `armazem.pronto` antes de
 * responder; aqui quem chama já tem o mais recente de cada pessoa em mãos e já
 * sabe se o armazém chegou, então sobra só a comparação — que é a parte que
 * erra fácil: o corte é a meia-noite de hoje, e não "faz menos de 24 horas".
 */
export function disseHoje(quando?: Date | null): boolean {
  if (!quando) return false;
  const agora = new Date();
  return (
    quando.getFullYear() === agora.getFullYear() &&
    quando.getMonth() === agora.getMonth() &&
    quando.getDate() === agora.getDate()
  );
}
